function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Pure helpers that turn what the user typed plus the browsing context into the exact body the
 * server's `validateFeedbackPayload` accepts. Kept free of store/ajax imports so it can be
 * reasoned about and tested in isolation.
 */
import { v4 as uuidV4 } from 'uuid';
import { GENERAL_CATEGORY_ID, IDEA_CATEGORY_ID, PROBLEM_CATEGORY_ID, PRODUCT_CATEGORY_ID, YES_NO_VALUES } from './feedbackConstants';

/** Every feedback form type keeps the fields the user fills in under this content path. */
export const FEEDBACK_CONTENT_ROOT = 'general';
const toTrimmedString = value => value === undefined || value === null ? '' : String(value).trim();

/**
 * The app runs on a hash router, so the meaningful route lives in `location.hash`. Fall back to
 * the pathname for the few path-based entry points (for example the auth callback page).
 */
export const getRouteFromLocation = ({
  hash: _hash = '',
  pathname: _pathname = ''
} = {}) => {
  if (_hash && _hash.startsWith('#')) {
    const [route] = _hash.slice(1).split('?');
    return route || '/';
  }
  return _pathname || '/';
};

/**
 * The server stores `browser` as a single string, so viewport and language are folded in here
 * rather than sent as a nested object.
 */
export const buildBrowserDescriptor = ({
  userAgent: _userAgent = '',
  language: _language = '',
  viewportWidth: _viewportWidth = null,
  viewportHeight: _viewportHeight = null
} = {}) => [_userAgent, _language, _viewportWidth && _viewportHeight ? `${_viewportWidth}x${_viewportHeight}` : ''].filter(Boolean).join(' | ');
export const buildDisplayContext = ({
  href: _href = '',
  hash: _hash2 = '',
  pathname: _pathname2 = '',
  userAgent: _userAgent2 = '',
  language: _language2 = '',
  viewportWidth: _viewportWidth2 = null,
  viewportHeight: _viewportHeight2 = null,
  timestamp: _timestamp = ''
} = {}) => ({
  route: getRouteFromLocation({
    hash: _hash2,
    pathname: _pathname2
  }),
  url: _href,
  browser: buildBrowserDescriptor({
    userAgent: _userAgent2,
    language: _language2,
    viewportWidth: _viewportWidth2,
    viewportHeight: _viewportHeight2
  }),
  // Shown to the user so they can see what moment the report refers to. The persisted timestamp
  // is owned by the server, so this is never sent.
  timestamp: _timestamp
});

/**
 * Reads the context from a global-like object. The scope is injected so callers (and tests) can
 * supply a plain object instead of patching globals.
 */
export const readDisplayContext = (globalScope = global) => {
  const {
    location = {},
    navigator = {},
    innerWidth = null,
    innerHeight = null
  } = globalScope || {};
  return buildDisplayContext({
    href: location.href,
    hash: location.hash,
    pathname: location.pathname,
    userAgent: navigator.userAgent,
    language: navigator.language,
    viewportWidth: innerWidth,
    viewportHeight: innerHeight,
    timestamp: new Date().toISOString()
  });
};

/**
 * Option pickers - both `optionPickerField` and `optionPickerButton` - persist a selection as
 * `{ <identifier>: { ...option } }`, so the value the server expects is the key of that object.
 */
export const readSelectedOptionIdentifier = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return '';
  }
  const [identifier = ''] = Object.keys(value);
  return identifier;
};

/**
 * Read what the user typed out of the draft content the generic editor maintains. All three
 * feedback form types keep their fields under `/general`.
 */
export const readFeedbackValues = (content = {}) => {
  const {
    description,
    reaction,
    comment,
    weldingEfficient,
    assetsEfficient
  } = (content === null || content === void 0 ? void 0 : content[FEEDBACK_CONTENT_ROOT]) || {};
  return {
    description: toTrimmedString(description),
    reaction: readSelectedOptionIdentifier(reaction),
    comment: toTrimmedString(comment),
    weldingEfficient: readSelectedOptionIdentifier(weldingEfficient),
    assetsEfficient: readSelectedOptionIdentifier(assetsEfficient)
  };
};
export const canSubmitFeedback = ({
  categoryId,
  values: _values = {},
  isSubmitting: _isSubmitting = false
} = {}) => {
  if (_isSubmitting) {
    return false;
  }
  // General and product feedback are deliberately submittable with no answers filled in.
  if (categoryId === GENERAL_CATEGORY_ID || categoryId === PRODUCT_CATEGORY_ID) {
    return true;
  }
  return Boolean(toTrimmedString(_values.description));
};

/**
 * Resolve screenshot contract fields for the payload.
 *
 * @param {string} categoryId
 * @param {{ screenshotReference?: string }} [screenshot={}]
 */
export const getScreenshotFields = (categoryId, screenshot = {}) => {
  const reference = toTrimmedString(screenshot.screenshotReference);
  const isProblemWithImage = categoryId === PROBLEM_CATEGORY_ID && Boolean(reference);
  if (isProblemWithImage) {
    return {
      screenshotAttached: true,
      screenshotReference: reference
    };
  }
  return {
    screenshotAttached: false
  };
};
const buildCommonFields = ({
  categoryId,
  context: _context = {},
  reference,
  screenshot: _screenshot = {},
  diagnostics: _diagnostics = ''
}) => _objectSpread(_objectSpread({
  category: categoryId,
  // Prefer the hash route (query already stripped). Never send `context.url` — full href can
  // carry tokens or PII in query strings into the superadmin Feedback cabinet.
  page: _context.route || '',
  browser: _context.browser || '',
  reference
}, getScreenshotFields(categoryId, _screenshot)), categoryId === PROBLEM_CATEGORY_ID && toTrimmedString(_diagnostics) ? {
  diagnostics: toTrimmedString(_diagnostics)
} : {});
const buildCategoryFields = (categoryId, values) => {
  if (categoryId === PROBLEM_CATEGORY_ID || categoryId === IDEA_CATEGORY_ID) {
    return {
      description: toTrimmedString(values.description)
    };
  }
  if (categoryId === PRODUCT_CATEGORY_ID) {
    const comment = toTrimmedString(values.comment);
    const weldingEfficient = toTrimmedString(values.weldingEfficient);
    const assetsEfficient = toTrimmedString(values.assetsEfficient);
    return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, values.reaction ? {
      reaction: values.reaction
    } : {}), YES_NO_VALUES.includes(weldingEfficient) ? {
      weldingEfficient
    } : {}), YES_NO_VALUES.includes(assetsEfficient) ? {
      assetsEfficient
    } : {}), comment ? {
      comment
    } : {});
  }
  const comment = toTrimmedString(values.comment);
  return _objectSpread(_objectSpread({}, values.reaction ? {
    reaction: values.reaction
  } : {}), comment ? {
    comment
  } : {});
};
export const buildFeedbackPayload = ({
  categoryId,
  values: _values2 = {},
  context: _context2 = {},
  reference: _reference = uuidV4().toUpperCase(),
  screenshot: _screenshot2 = {},
  diagnostics: _diagnostics2 = ''
}) => _objectSpread(_objectSpread({}, buildCommonFields({
  categoryId,
  context: _context2,
  reference: _reference,
  screenshot: _screenshot2,
  diagnostics: _diagnostics2
})), buildCategoryFields(categoryId, _values2));